import { Box, Button, Center, Flex, Icon, NotificationCounter, Tooltip, VStack } from '@televet/kibble-ui';
import { SuccessCenterIconBlack } from 'assets/icons';
import partial from 'lodash-es/partial';
import MobileMenu from 'pages/MobileMenu';
import { CountryCode } from 'pages/Settings/pages/GeneralInfoSettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SidePanelScheduler from 'shared/components/SidePanel/components/SchedulerPanel';
import SidePanelSearch from 'shared/components/SidePanel/components/SearchPanel';
import checkBrowserSupport, {
  getTooltipMessage,
} from 'shared/components/UnsupportedBrowserPopover/checkBrowserSupport';
import { FeatureFlagName } from 'shared/enums';
import { BrowserSupportFeatures } from 'shared/enums/BrowserSupportFeatures';
import { HasModules } from '../HasModule';
import { ModuleName } from '@televet/shared-types/productPackages';
import { CallTypes } from 'shared/enums/CallTypes';
import useClinicUser from 'shared/hooks/useClinicUser';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { useAuth } from 'shared/providers/AuthProvider';
import { usePhoneCallContext } from 'shared/providers/PhoneCallProvider';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ChurnZeroEvents, PhonCallPopoverEvents } from 'shared/providers/TrackingProvider/EventNames';
import { Role } from 'shared/types/graphql';
import HasFeature from '../HasFeature';
import { CareGoalTracker } from './components/CareGoalTracker';
import InvoicingPanel from './components/InvoicingPanel';
import MassTextAlertsPanel from './components/MassTextAlertsPanel';
import PanelButton from './shared/components/PanelButton';
import { PanelViews } from './shared/types/enums/PanelViews';
import { useSidePanel } from './state/providers/SidePanelProvider';
import { useModularity } from 'shared/hooks/useModularity';
import { show as openIntercomMessenger } from '@intercom/messenger-js-sdk';


const SidePanel = (): JSX.Element => {
  const { isOpen, setIsOpen, currentView, setCurrentView, isZIndexIncreased } = useSidePanel();
  const { isImpersonating } = useAuth();
  const { clinicUser, currentClinic } = useClinicUser();
  const { showPhone } = usePhoneCallContext();
  const { isMobile } = useResolutionProvider();
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);
  const isAdmin = useMemo(() => clinicUser?.vetInfo?.roles?.some((r) => r.role === Role.Admin), [clinicUser]);
  const [lastPannelView, setLastPanelView] = useState<PanelViews>();
  const isSmsEnabled = currentClinic?.hasSmsConversations;
  const { isBrowserSupported, OS } = useMemo(() => {
    return checkBrowserSupport(BrowserSupportFeatures.Voice);
  }, []);
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const { trackEvent } = useTracking();
  const [notificationNumber, setNotificationNumber] = useState(0);

  const isPendoEnabled = useMemo(() => isFeatureEnabled(FeatureFlagName.EnablePendo), [isFeatureEnabled]);

  useEffect(() => {
    setTimeout(() => {
      if (window.zE) {
        window.zE('messenger:on', 'unreadMessages', (count: number) => {
          setNotificationNumber(count);
        });
      }
    }, 1000);
  }, []);

  const phoneTooltipMessage = useMemo(() => {
    if (!isBrowserSupported) {
      return getTooltipMessage(CallTypes.Phone, OS);
    }

    return 'Open dial pad';
  }, [isBrowserSupported, OS]);

  const shouldShowMassTextAlertsIcon = useMemo(() => {
    return isSmsEnabled && isAdmin;
  }, [isSmsEnabled, isAdmin]);

  const handleSidePanelToggle = useCallback(
    (panelView?: PanelViews) => {
      if (panelView) setCurrentView(panelView);

      if (panelView === undefined && currentView !== PanelViews.None) {
        setLastPanelView(currentView);
      } else if (panelView !== undefined && currentView !== PanelViews.None) {
        setLastPanelView(panelView);
      }

      if (!panelView || !isOpen || (isOpen && panelView === currentView)) {
        const isOpenCopy = !isOpen;
        setIsOpen(isOpenCopy);
        !isOpenCopy && setCurrentView(PanelViews.None);
      }

      if (!panelView) {
        const fallbackView = isStandardClinic ? PanelViews.Search : PanelViews.Scheduler;
        currentView !== PanelViews.None && isOpen && setCurrentView(PanelViews.None);
        currentView === PanelViews.None &&
          !isOpen &&
          setCurrentView(!!lastPannelView ? lastPannelView : fallbackView);
      }
    },
    [setCurrentView, currentView, isOpen, setIsOpen, isStandardClinic, lastPannelView],
  );

  const handleZendeskClick = (): void => {

    if (isImpersonating) {
      return;
    }
    trackEvent(ChurnZeroEvents.ChatWithUsClicked);
    if (isStandardClinic) {
      window?.zE?.('messenger', 'open');
    } else {
      openIntercomMessenger();
    }
  };

  const triggerSuccessCenter = useCallback((): void => {
    try {
      window.ChurnZero?.push(['open']);
      setTimeout(() => {
        // In a likely scenario where ChurnZero fails to add their iframe (they suck!)
        // we'll fallback to to the Zendesk chat
        if (!document.getElementById('cz_success_center_frame')) {
          trackEvent(ChurnZeroEvents.FailedToLoad);
        } else {
          trackEvent(ChurnZeroEvents.Loaded);
          if (window.zE) {
            window.zE('messenger', 'open');
            setTimeout(() => {
              window.zE('messenger', 'close');
            }, 200);
          }
        }
      }, 200);
    } catch (e) {
      console.log(e);
      trackEvent(ChurnZeroEvents.ErrorOnLoad);
    }
    return;
  }, [trackEvent]);

  const triggerPhonePopover = useCallback(async () => {
    trackEvent(PhonCallPopoverEvents.ViewPopover);
    showPhone();
  }, [showPhone, trackEvent]);

  const isUSClinic = useMemo(() => {
    if (!clinicUser?.vetInfo?.currentClinic?.country) return true;
    return clinicUser?.vetInfo?.currentClinic?.country === CountryCode.US;
  }, [clinicUser?.vetInfo?.currentClinic?.country]);

  return (
    <Flex
      className="SidePanel__Container"
      flex="0 0 auto"
      height="100%"
      background="background.default"
      zIndex={isZIndexIncreased ? 1500 : 1}
      align="flex-start"
      justify="flex-end"
      {...(isOpen && { borderLeft: '1px', borderColor: 'border.default' })}
      transition="width 0.2s cubic-bezier(0.87, 0, 0.13, 1)"
      sx={{
        '@media (max-width: 850px)': {
          width: isOpen ? '100%' : '0px',
          overflowY: 'auto',
          overflowX: 'hidden',
        },
      }}
    >
      {isOpen && (
        <VStack
          className="SidePanel__PanelView"
          spacing={0}
          w={{ base: '100%', md: '350px' }}
          height="100%"
          justify="flex-start"
          align="stretch"
        >
          {((): JSX.Element | null => {
            switch (currentView) {
              case PanelViews.Scheduler:
                return <SidePanelScheduler />;
              case PanelViews.Search:
                return <SidePanelSearch />;
              case PanelViews.Invoicing:
                return <InvoicingPanel />;
              case PanelViews.Menu:
                return <MobileMenu />;
              case PanelViews.MassTextAlerts:
                return <MassTextAlertsPanel />;
              default:
                return null;
            }
          })()}
        </VStack>
      )}
      {!isMobile && (
        <VStack
          className="SidePanel__NavButtons"
          justify="space-between"
          h="100%"
          borderLeft="1px"
          borderColor="border.default"
          pb={2}
        >
          <VStack spacing={0}>
            <Center h={14} p={2} w="100%">
              <Button
                iconName={isOpen ? 'moveRight' : 'moveLeft'}
                variant="ghostNeutral"
                onClick={partial(handleSidePanelToggle, undefined)}
                data-mixpanel-name="Right panel collapse icon"
                data-testid="right-panel-collapse-icon-button"
                id="sidepanel-toggle-button"
              />
            </Center>
            <HasModules modulesToCheck={[ModuleName.AppointmentsStandard, ModuleName.AppointmentsRecap]}>
              <PanelButton
                isCurrentView={currentView === PanelViews.Scheduler}
                iconName="calendarCheck"
                onClick={partial(handleSidePanelToggle, PanelViews.Scheduler)}
                title="Schedule"
                data-mixpanel-name="Right panel calendar icon"
                data-testid="right-panel-calendar-icon-button"
                id="sidepanel-scheduler-button"
              />
            </HasModules>
            <HasModules modulesToCheck={[ModuleName.AppointmentsStandard]}>
              <PanelButton
                isCurrentView={currentView === PanelViews.Search}
                iconName="magnifyingGlass"
                onClick={partial(handleSidePanelToggle, PanelViews.Search)}
                title="Search"
                data-mixpanel-name="Right panel patient search icon"
                data-testid="right-panel-patient-search-icon-button"
                id="sidepanel-search-button"
              />
            </HasModules>

            {isUSClinic && (
              <HasModules modulesToCheck={[ModuleName.AppointmentsStandard]}>
                <PanelButton
                  isCurrentView={currentView === PanelViews.Invoicing}
                  iconName="creditCard"
                  onClick={partial(handleSidePanelToggle, PanelViews.Invoicing)}
                  title="Payments"
                  data-mixpanel-name="Right panel invoicing icon"
                  data-testid="right-panel-invoicing-icon-button"
                  id="sidepanel-invoice-button"
                />
              </HasModules>
            )}

            {shouldShowMassTextAlertsIcon && (
              <HasModules modulesToCheck={[ModuleName.AppointmentsStandard]}>
                <PanelButton
                  isCurrentView={currentView === PanelViews.MassTextAlerts}
                  iconName="messageWarning"
                  onClick={partial(handleSidePanelToggle, PanelViews.MassTextAlerts)}
                  title="Mass Text Alerts"
                  data-mixpanel-name="Right panel mass text alert icon"
                  data-testid="right-panel-mass-text-alert-icon"
                  id="sidepanel-mass-text-alert-button"
                />
              </HasModules>
            )}
          </VStack>

          <HasFeature name={FeatureFlagName.CarePlans}>
            <HasModules modulesToCheck={[ModuleName.AppointmentsStandard]}>
              <CareGoalTracker />
            </HasModules>
          </HasFeature>

          <VStack>
            {isBrowserSupported && (
              <HasModules modulesToCheck={[ModuleName.AppointmentsStandard]}>
                <PanelButton
                  iconName="phoneButtons"
                  title={phoneTooltipMessage}
                  isCurrentView={false}
                  onClick={triggerPhonePopover}
                />
              </HasModules>
            )}
            <Box
              data-mixpanel-name="Right panel help icon"
              data-testid="right-panel-help-icon"
              id="zendesk-trigger"
              onClick={handleZendeskClick}
              position="relative"
              cursor="pointer"
            >
              {!!notificationNumber && (
                <Flex position="absolute" top={-1} left={-1}>
                  <NotificationCounter count={notificationNumber} showCount size="md" />
                </Flex>
              )}
              <Icon name="questionMarkCircle" size="xl" />
            </Box>
            <Box
              display={isFeatureFlagsLoading || !isPendoEnabled ? 'none' : 'block'}
              zIndex="overlay"
              position="relative"
            >
              <Tooltip label="Resource Center" placement="left">
                <Button
                  iconName="logo"
                  data-mixpanel-name="Resource Center Trigger"
                  data-testid="resource-center-trigger"
                  id="pendo-resource-center-trigger"
                  size="sm"
                />
              </Tooltip>
            </Box>
            <Box display={isFeatureFlagsLoading || isPendoEnabled ? 'none' : 'block'}>
              <Tooltip label="Success Hub" placement="left">
                <Flex
                  data-mixpanel-name="Success Hub trigger"
                  data-testid="success-center-trigger"
                  id="success-center-trigger"
                  onClick={triggerSuccessCenter}
                  w={8}
                  align="center"
                  justify="center"
                  cursor="pointer"
                >
                  <SuccessCenterIconBlack />
                </Flex>
              </Tooltip>
            </Box>
          </VStack>
        </VStack>
      )}
    </Flex>
  );
};

export default SidePanel;
